import React from "react"
import PropTypes from "prop-types"
import {
    Card,
    CardContent,
    Stack,
    CardMedia,
    Typography,
    Box,
    Tooltip,
    useMediaQuery,
    useTheme,
} from "@mui/material"
import { styled } from "@mui/system"

const StyledCard = styled(Card)(({ theme }) => ({
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
        boxShadow: theme.shadows[6],
    },
    display: "flex",
    height: "100%",
    minHeight: 100,
    width: "100%", // Ensure the card takes full width of its container
}))

const TruncatedTypography = styled(Typography)({
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
})

const SecondaryPropertyCard = ({
    image,
    price,
    beds,
    baths,
    sqft,
    acres,
    hoa,
    address,
    url,
    listingType,
}) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

    const handleClick = () => {
        window.open(url, "_blank", "noopener,noreferrer")
    }

    const formatPrice = (price) => {
        if (!price) return '';
        
        // If price is already formatted (contains $), return as is
        if (price.toString().includes('$')) {
            return price;
        }
        
        // Convert to number and format
        const numericValue = parseFloat(price);
        if (isNaN(numericValue)) return price;
        
        const formatted = `$${numericValue.toLocaleString('en-US')}`;

        // Append /month only for past rentals
        if (listingType === 'past-rentals') {
            return `${formatted}/month`;
        }

        return formatted;
    }

    const renderPropertyDetails = () => {
        const details = [
            beds && `${beds} Bed`,
            baths && `${baths} Bath`,
            sqft && `${sqft.toLocaleString()} Sq Ft`,
            acres && `${acres} Acres`,
            hoa && `HOA ${hoa}`,
        ].filter(Boolean)

        return (
            <Stack direction="row" spacing={0.5} flexWrap="nowrap">
                {details.map((detail, index) => (
                    <React.Fragment key={index}>
                        <TruncatedTypography
                            variant="body2"
                            color="text"
                            noWrap
                        >
                            {detail}
                        </TruncatedTypography>
                        {index < details.length - 1 && (
                            <TruncatedTypography variant="body2" color="text">
                                •
                            </TruncatedTypography>
                        )}
                    </React.Fragment>
                ))}
            </Stack>
        )
    }

    return (
        <StyledCard onClick={handleClick}>
            <CardMedia
                component="img"
                sx={{ width: 100, flexShrink: 0 }}
                image={image}
                alt={address}
            />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    overflow: "hidden",
                }}
            >
                <CardContent sx={{ flex: "1 0 auto", px: 2 }}>
                    <Typography
                        component="div"
                        variant={isMobile ? "subtitle1" : "h6"}
                        noWrap
                        fontWeight="bold"
                    >
                        {formatPrice(price)}
                    </Typography>
                    {renderPropertyDetails()}
                    <Tooltip title={address}>
                        <TruncatedTypography
                            variant="body2"
                            color="text.secondary"
                        >
                            {address}
                        </TruncatedTypography>
                    </Tooltip>
                </CardContent>
            </Box>
        </StyledCard>
    )
}

SecondaryPropertyCard.propTypes = {
    image: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    beds: PropTypes.number,
    baths: PropTypes.number,
    sqft: PropTypes.number,
    acres: PropTypes.string,
    hoa: PropTypes.string,
    address: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    listingType: PropTypes.string,
}

export default SecondaryPropertyCard
